import { FC, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Video, { StreamType } from 'components/Video/Video'
import { getStreams } from 'services/stream'
import { getRouteConfig, getConfig } from 'services/config'
import { checkVideoUrlAvailability } from 'utils/video'

import styles from './mlsScreen.module.scss'

interface Props {
  logo?: string
  client?: string
  routeName?: string
}

const MlsScreen: FC<Props> = ({ logo, client, routeName }) => {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const [wakeLock, setWakeLock] = useState(null)
  const [streamId, setStreamId] = useState(params?.get('streamId') ?? null)
  const [mlsConfig, setMlsConfig] = useState<any>(null)
  const [videoUrl, setVideoUrl] = useState<string | undefined>(undefined)
  const sourceId = params?.get('sourceId')
  const videoId = params?.get('video_id')
  const videoExt = params?.get('videoExt') ?? 'mp4'
  const clientName = client ?? params?.get('clientName') ?? 'sportbuff'
  const isPreGameMode = params?.get('pregameMode') ?? false
  const preGameFrequency = params?.get('buffFrequency') ?? 10
  const anonymousLogin = params?.get('anonymousLogin') !== 'false'
  const token = params?.get('userToken') ?? null
  const widgetInstance = useRef<any>()
  const sportBuffContainer = useRef<HTMLElement>()
  const container = useRef<HTMLDivElement>(null)
  const streamType =
    params?.get('isVOD') === 'true' ? StreamType.VOD : StreamType.LIVE

  const onTabFocus = async () => {
    try {
      if (!wakeLock) {
        // @ts-ignore
        const lockObj = await navigator.wakeLock.request('screen')
        setWakeLock(lockObj as any)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const onTabBlur = async () => {
    if (wakeLock) {
      // @ts-ignore
      await wakeLock.release()
      setWakeLock(null)
    }
  }

  const init = async (_config: any) => {
    if (!_config?.streamId && !_config?.streamSourceId) return

    const handleMouseOut = () => {
      widgetInstance?.current?.controls.hideUi()
    }

    const handleMouseOver = () => {
      widgetInstance?.current?.controls.showUi()
    }

    const appendContainer = (containerToAppend: HTMLElement) => {
      sportBuffContainer.current = containerToAppend
      container.current?.appendChild?.(containerToAppend)
    }

    const destroyContainer = () => {
      if (!sportBuffContainer.current) return
      container?.current?.removeChild?.(sportBuffContainer.current)

      container?.current?.removeEventListener('mouseover', handleMouseOver)
      container?.current?.removeEventListener('mouseout', handleMouseOut)
    }

    container?.current?.addEventListener('mouseover', handleMouseOver)
    container?.current?.addEventListener('mouseout', handleMouseOut)

    const widgetConfig = {
      ..._config,
      appendContainer,
      destroyContainer,
    }

    const instance = await window?.SportBuff.init(widgetConfig)
    widgetInstance.current = instance
  }

  useEffect(() => {
    const script = document.createElement('script')

    const scriptUrl = ['develop', 'qa', 'sportbuff'].includes(clientName)
      ? `https://buffup-web-sdk.core.buffup.net/buffup-web-sdk/${clientName}/latest/bundle-main.min.js`
      : `https://buffup-web-sdk.core.buffup.net/buffup-web-sdk/${clientName}/6.x/bundle-main.min.js`
    script.setAttribute('src', scriptUrl)
    script.setAttribute('type', 'text/javascript')

    document.head.appendChild(script)

    setTimeout(() => {
      const buffContainer = document.getElementById('SportBuff-container')
      if (buffContainer) buffContainer.style.zIndex = '1000'
    }, 2000)

    return () => {
      window.removeEventListener('focus', onTabFocus)
      window.removeEventListener('blur', onTabBlur)
    }
  }, [])

  useEffect(() => {
    const fetchStreams = async () => {
      const streams = await getStreams(clientName)
      const firstLiveStream = await streams?.find((s) => {
        return streamType === StreamType.VOD
          ? s.endedAt && s.liveAt
          : !s.endedAt && s.liveAt
      })
      setStreamId(firstLiveStream?.id ?? '')
    }

    if (!streamId && !sourceId) fetchStreams()

    let config: any = {
      player: 'custom-functions',
      clientName,
    }

    if (token) {
      config = {
        ...config,
        token,
      }
    }

    if (sourceId && !streamId) {
      config = {
        ...config,
        streamSourceId: sourceId,
      }
    }

    if (!sourceId && streamId) {
      config = {
        ...config,
        streamId,
      }
    }

    if (isPreGameMode) {
      config = {
        ...config,
        preStreamBuffFrequency: preGameFrequency,
        anonymousLogin,
      }
    }

    // cludge for anonymous login based on query param
    config = {
      ...config,
      anonymousLogin,
    }

    if (window.SportBuff) {
      if (widgetInstance.current) {
        widgetInstance.current.destroy()
      }

      setTimeout(() => {
        init(config)
      }, 200)
    } else {
      ;(window as any).onSportBuffReady = () => {
        init(config)
      }
    }
  }, [streamId, sourceId])

  function handleWindowSizeChange() {
    // setWidth(window.innerWidth)
  }

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange)
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange)
    }
  }, [])

  // Determine video URL based on priority order
  useEffect(() => {
    const determineVideoUrl = async () => {
      // Priority 1: If video_id is provided, check buffup-public.prod.buffup.net first
      if (videoId) {
        const buffupPublicUrl = `https://buffup-public.prod.buffup.net/video/${videoId}.${videoExt}`
        const isAvailable = await checkVideoUrlAvailability(buffupPublicUrl)

        if (isAvailable) {
          setVideoUrl(buffupPublicUrl)
          console.log('videoUrl (from buffup-public):', buffupPublicUrl)
          return
        }

        // Priority 2: If not available, check REACT_APP_CONFIG_S3_PATH
        const configS3Path = process.env.REACT_APP_CONFIG_S3_PATH
        if (configS3Path) {
          const s3Url = `${configS3Path}/${videoId}.${videoExt}`
          const isS3Available = await checkVideoUrlAvailability(s3Url)

          if (isS3Available) {
            setVideoUrl(s3Url)
            console.log('videoUrl (from S3 config path):', s3Url)
            return
          }
        }
      }

      // Priority 3: Fall back to video_url from config JSON
      if (mlsConfig?.video_url) {
        setVideoUrl(mlsConfig.video_url)
        console.log('videoUrl (from config JSON):', mlsConfig.video_url)
        return
      }

      // No video URL available
      setVideoUrl(undefined)
    }

    determineVideoUrl()
  }, [videoId, videoExt, mlsConfig])

  // Fetch config from S3 service and extract route configuration
  useEffect(() => {
    const loadConfig = async () => {
      // Only fetch if routeName is provided
      if (!routeName) {
        return
      }

      try {
        // Get the full config data using the cached service
        const configData = await getConfig()

        // Get the complete route configuration object using the routeName
        const routeConfigData = await getRouteConfig(routeName)

        // Set the route config state
        setMlsConfig(routeConfigData)

        // Console log the route config data
        console.log(`${routeName} config from S3:`, routeConfigData)
        console.log('Full config data:', configData)
      } catch (error) {
        // Log any errors that occur during the fetch
        console.error('Error fetching config from S3:', error)
      }
    }

    // Call the load function
    loadConfig()
  }, [routeName])

  return (
    <>
      <div
        id="buff-video-container"
        className={styles.container}
        ref={container}
      >
        {/* Render video component as background if video_url is available */}
        {videoUrl && (
          <div className={styles.videoBackground}>
            <Video videoUrl={videoUrl} streamType={StreamType.VOD} loop />
          </div>
        )}
        <header className={styles.header}>
          <div className={(styles.headerSection, styles.center)}>
            <img src={mlsConfig?.logo || logo} alt="logo" />
          </div>
        </header>
        <div
          className={styles.textContent}
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            alignSelf: 'center',
            zIndex: '100',
            padding: '20px',
          }}
        >
          <h2
            style={{
              color: 'white',
              fontWeight: 700,
              width: '100%',
              textAlign: 'center',
              alignSelf: 'center',
              zIndex: '1001',
            }}
          >
            {mlsConfig?.title || 'Answer and be part of the show'}
          </h2>
          <h2
            style={{
              color: 'white',
              fontWeight: 400,
              width: '100%',
              textAlign: 'center',
              alignSelf: 'center',
              zIndex: '1001',
            }}
          >
            {mlsConfig?.description || 'Your opinion counts'}
          </h2>
        </div>
      </div>
    </>
  )
}

export default MlsScreen

MlsScreen.defaultProps = {
  logo: undefined,
  client: undefined,
  routeName: undefined,
}
